import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search, X, Loader2 } from 'lucide-react';
import { cn } from '../utils/cn';

export interface SearchableSelectOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

export interface SearchableSelectProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  /** Static option list. Ignored when `loadOptions` is provided. */
  options?: SearchableSelectOption[];
  /**
   * Async loader. Called once on first open; options are cached thereafter.
   * Any subsequent error surfaces inline.
   */
  loadOptions?: () => Promise<SearchableSelectOption[]>;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyLabel?: string;
  clearable?: boolean;
  disabled?: boolean;
  error?: string;
  className?: string;
  required?: boolean;
  /**
   * When true, user can press Enter on the search query to commit it as the
   * value (even if it doesn't match any loaded option). Useful for fields
   * like Brand where users may type a new value that doesn't yet exist.
   */
  allowCustom?: boolean;
}

export function SearchableSelect({
  label,
  value,
  onChange,
  options: staticOptions,
  loadOptions,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  emptyLabel = 'No matches',
  clearable = true,
  disabled,
  error,
  className,
  required,
  allowCustom,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [loadedOptions, setLoadedOptions] = useState<SearchableSelectOption[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const fetchedRef = useRef(false);
  const [menuPos, setMenuPos] = useState<{ left: number; width: number; maxH: number; top?: number; bottom?: number } | null>(null);

  // Position the dropdown — rendered in a portal on <body> so it can't be
  // clipped by an `overflow` ancestor (e.g. a scrolling line-items table,
  // which made the menu "go below the page"). Re-place on scroll/resize.
  useEffect(() => {
    if (!open) { setMenuPos(null); return; }
    const place = () => {
      const r = triggerRef.current?.getBoundingClientRect();
      if (!r) return;
      const below = window.innerHeight - r.bottom;
      const above = r.top;
      const openUp = below < 200 && above > below;
      setMenuPos({
        left: r.left,
        width: r.width,
        maxH: Math.max(140, Math.min(300, (openUp ? above : below) - 16)),
        ...(openUp ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }),
      });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  // Close on outside click — but NOT when clicking inside the portaled menu
  // (which lives outside `ref`).
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const options = staticOptions ?? loadedOptions ?? [];

  // Trigger the async loader as soon as either:
  //   (a) the user opens the dropdown for the first time, OR
  //   (b) the form mounts with a `value` already set (edit mode) so the
  //       label can render without waiting for the user to click — this
  //       fixes the "Category dropdown shows placeholder when editing"
  //       bug where the saved UUID had no matching option to print.
  useEffect(() => {
    if (!loadOptions || fetchedRef.current) return;
    if (!open && !value) return;
    fetchedRef.current = true;
    setLoading(true);
    setLoadError(null);
    loadOptions()
      .then((opts) => setLoadedOptions(opts))
      .catch((e: unknown) => {
        fetchedRef.current = false;
        const msg = e instanceof Error ? e.message : 'Failed to load options';
        setLoadError(msg);
      })
      .finally(() => setLoading(false));
  }, [open, value, loadOptions]);

  const filtered = useMemo(() => {
    if (!query) return options;
    const q = query.toLowerCase();
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        (o.description?.toLowerCase().includes(q) ?? false)
    );
  }, [options, query]);

  const selected = options.find((o) => o.value === value);

  const clear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
  };

  const pick = (v: string) => {
    onChange(v);
    setOpen(false);
    setQuery('');
  };

  const commitCustom = () => {
    const q = query.trim();
    if (!q) return;
    if (options.some((o) => o.value.toLowerCase() === q.toLowerCase())) return;
    pick(q);
  };

  const canCommitCustom =
    !!allowCustom &&
    !!query.trim() &&
    !options.some(
      (o) =>
        o.value.toLowerCase() === query.trim().toLowerCase() ||
        o.label.toLowerCase() === query.trim().toLowerCase()
    );

  return (
    <div className={cn('flex w-full flex-col gap-1', className)} ref={ref}>
      {label && (
        <span className="text-xs font-medium text-content-secondary">
          {label} {required && <span className="text-danger">*</span>}
        </span>
      )}
      <div
        ref={triggerRef}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={cn(
          'flex h-10 w-full cursor-pointer items-center gap-2 rounded-md border px-3 transition-colors',
          open ? 'border-primary' : 'border-border',
          error && 'border-danger',
          disabled && 'cursor-not-allowed opacity-50',
          'bg-surface-card'
        )}
      >
        <span
          className={cn(
            'flex-1 truncate text-sm',
            selected || (allowCustom && value) || (value && loading)
              ? 'text-content-primary'
              : 'text-content-muted'
          )}
        >
          {selected
            ? selected.label
            : allowCustom && value
              ? value
              : value && loading
                ? 'Loading…'
                : placeholder}
        </span>
        {clearable && (selected || (allowCustom && value)) && !disabled && (
          <button
            type="button"
            onClick={clear}
            aria-label="Clear"
            className="flex h-5 w-5 items-center justify-center rounded-full text-content-muted hover:bg-surface-card-hover hover:text-content-primary"
          >
            <X className="h-3 w-3" />
          </button>
        )}
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-content-muted transition-transform',
            open && 'rotate-180'
          )}
        />
      </div>

      {error && <span className="text-xs text-danger">{error}</span>}

      {open && menuPos && createPortal(
        <div
          ref={menuRef}
          style={{ position: 'fixed', left: menuPos.left, width: menuPos.width, top: menuPos.top, bottom: menuPos.bottom, zIndex: 100 }}
          className="rounded-md border border-border bg-surface-card shadow-xl"
        >
            <div className="flex items-center gap-2 border-b border-border px-3 py-2">
              <Search className="h-3.5 w-3.5 text-content-muted" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setOpen(false);
                    setQuery('');
                  } else if (e.key === 'Enter' && canCommitCustom) {
                    e.preventDefault();
                    commitCustom();
                  }
                }}
                className="w-full bg-transparent text-sm text-content-primary outline-none placeholder:text-content-muted"
              />
              {loading && <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-content-muted" />}
            </div>

            <div className="overflow-y-auto py-1" style={{ maxHeight: menuPos.maxH }}>
              {loadError && (
                <div className="px-3 py-2 text-xs text-danger">{loadError}</div>
              )}
              {canCommitCustom && (
                <button
                  type="button"
                  onClick={commitCustom}
                  className="flex w-full items-center gap-2 border-b border-border/60 px-3 py-2 text-left text-sm text-primary hover:bg-primary-light"
                >
                  <span className="text-[11px] font-semibold uppercase tracking-wide">Use</span>
                  <span className="flex-1 truncate font-medium">"{query.trim()}"</span>
                  <span className="text-[10px] text-content-muted">Enter</span>
                </button>
              )}
              {!loading && !loadError && filtered.length === 0 && !canCommitCustom && (
                <div className="px-3 py-2 text-xs text-content-muted">
                  {emptyLabel}
                </div>
              )}
              {filtered.map((opt) => {
                const isSelected = opt.value === value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    disabled={opt.disabled}
                    onClick={() => pick(opt.value)}
                    className={cn(
                      'flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm transition-colors disabled:opacity-40',
                      isSelected
                        ? 'bg-primary-light text-primary font-medium'
                        : 'text-content-secondary hover:bg-surface-card-hover'
                    )}
                  >
                    <span className="w-full truncate">{opt.label}</span>
                    {opt.description && (
                      <span className="w-full truncate text-[11px] text-content-muted">
                        {opt.description}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
